import { useCallback, useEffect, useMemo, useRef } from 'react';
import {
  useMutation,
  useQuery,
  useQueryClient,
  useQueries,
} from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { notifications } from '@mantine/notifications';
import { appConfigs } from '@configs/apps/appConfigs';
import {
  DeviceConfigurationApi,
  SessionStatus,
  buildCompletedSessionConfigJson,
} from '@model/DeviceConfiguration';
import {
  getConfiguration,
  updateConfiguration,
  deleteConfiguration,
} from '@services/configurationManager';
import type {
  SessionInfoPayload,
  SessionEndedPayload,
  SessionStartedAtPayload,
  CancelSessionReceivedPayload,
  UpdateConfigurationRequest,
} from '@types';
import { getVideoById, getAudioById } from '@services/mediaManager';
import { useAbly } from '../providers/AblyProvider';
import { normalizeEvents, useHeartRateStore } from '../store/useHeartRateStore';
import { useBaselineCalculation } from './useBaselineCalculation';
import { useBaselineCalculationStore } from '@store/useBaselineCalculationStore';
import { createSessionEventManager } from '../utils/sessionEventManager';
import {
  getExposureConfigFromSession,
  normalizeInitSessionConfig,
} from '../utils/extractSessionObject';
import {
  formatExposureItemFeedback,
  formatExposureItemShown,
  resolveExposureItemFromPayload,
} from '../utils/exposureMultiItem';
import {
  extractMessageName,
  createSessionMessageFilter,
  handleSessionInfo,
  handleSessionEnded,
  handleSessionStartedAt,
  handleCancelSession,
  handleExposureEvent,
} from '../utils/sessionMessageHandlers';
import {
  transformSessionConfig,
  type TransformedSessionConfig,
  type TransformedVideoStep,
} from '../utils/transformSessionConfig';
import type { InitSessionConfigStep } from '@types';

/** Set condiviso tra tutte le subscription: evita toast duplicati quando più useSession(id) sono montati. */
const shownExposureToastKeys = new Set<string>();

const sessionQueryKey = (id: string | undefined) => ['session', id];

export type StartExperienceOptions = {
  /** Body Image: device is driven by `start_bodyimage`, not initSession/startSession. */
  skipDeviceStart?: boolean;
  /** Optimistic UI immediately; PATCH session status without blocking the caller. */
  persistInBackground?: boolean;
};

export const useSession = (id: string | undefined) => {
  const { sendMessage, subscribeToMessages } = useAbly();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  // React Query for session data
  const sessionQuery = useQuery({
    queryKey: sessionQueryKey(id),
    queryFn: async () => {
      if (!id) {
        throw new Error('Session ID is required');
      }
      const response = await getConfiguration(id);
      return response.data;
    },
    enabled: Boolean(id),
    staleTime: 30_000, // 30 seconds
    gcTime: 5 * 60 * 1000, // 5 minutes (previously cacheTime)
    refetchOnWindowFocus: false, // Evita refetch quando la finestra riprende focus
    refetchOnReconnect: false, // Evita refetch automatico al reconnect
    refetchOnMount: true, // Mantieni refetch al mount per avere dati freschi
  });

  // Normalizza la config in array di InitSessionConfigStep
  const normalizedConfig = useMemo<InitSessionConfigStep[]>(() => {
    if (!sessionQuery.data?.config) return [];
    return normalizeInitSessionConfig(sessionQuery.data.config);
  }, [sessionQuery.data?.config]);

  // Trasforma la config in oggetto con indici numerici
  const transformedConfigBase = useMemo<TransformedSessionConfig>(() => {
    return transformSessionConfig(normalizedConfig);
  }, [normalizedConfig]);

  // Estrai tutti gli ID di video e audio dalla config
  const mediaIds = useMemo(() => {
    const videoIds: (number | string)[] = [];
    const audioIds: (number | string)[] = [];

    Object.values(transformedConfigBase).forEach(step => {
      if ('videoId' in step && step.videoId) {
        const videoId = step.videoId;
        const numId = Number(videoId);
        if (!isNaN(numId) && numId > 0) {
          videoIds.push(numId);
        }
      }
      if ('audioId' in step && step.audioId) {
        const audioId = step.audioId;
        const numId = Number(audioId);
        if (!isNaN(numId) && numId > 0) {
          audioIds.push(numId);
        }
      }
    });

    return {
      videoIds: [...new Set(videoIds)],
      audioIds: [...new Set(audioIds)],
    };
  }, [transformedConfigBase]);

  // Fetch tutti i video necessari
  const videoQueries = useQueries({
    queries: mediaIds.videoIds.map(videoId => ({
      queryKey: ['video', videoId],
      queryFn: () => getVideoById(videoId),
      enabled: Boolean(videoId),
      staleTime: 5 * 60 * 1000,
      gcTime: 10 * 60 * 1000,
    })),
  });

  // Fetch tutti gli audio necessari
  const audioQueries = useQueries({
    queries: mediaIds.audioIds.map(audioId => ({
      queryKey: ['audio', audioId],
      queryFn: () => getAudioById(audioId),
      enabled: Boolean(audioId),
      staleTime: 5 * 60 * 1000,
      gcTime: 10 * 60 * 1000,
    })),
  });

  // Crea mappe per accesso rapido a video/audio per ID
  const videosMap = useMemo(() => {
    const map = new Map<number | string, (typeof videoQueries)[0]['data']>();
    mediaIds.videoIds.forEach((videoId, index) => {
      if (videoQueries[index]?.data) {
        map.set(videoId, videoQueries[index].data);
      }
    });
    return map;
  }, [mediaIds.videoIds, videoQueries]);

  const audiosMap = useMemo(() => {
    const map = new Map<number | string, (typeof audioQueries)[0]['data']>();
    mediaIds.audioIds.forEach((audioId, index) => {
      if (audioQueries[index]?.data) {
        map.set(audioId, audioQueries[index].data);
      }
    });
    return map;
  }, [mediaIds.audioIds, audioQueries]);

  // Popola la config trasformata con i video/audio completi
  const transformedConfig = useMemo<TransformedSessionConfig>(() => {
    const populated: TransformedSessionConfig = {};

    Object.entries(transformedConfigBase).forEach(([index, step]) => {
      if ('videoId' in step || 'audioId' in step) {
        const videoStep = step as TransformedVideoStep;
        const videoId = videoStep.videoId
          ? Number(videoStep.videoId)
          : undefined;
        const audioId = videoStep.audioId
          ? Number(videoStep.audioId)
          : undefined;

        populated[Number(index)] = {
          ...videoStep,
          video:
            videoId && !isNaN(videoId) ? videosMap.get(videoId) : undefined,
          audio:
            audioId && !isNaN(audioId) ? audiosMap.get(audioId) : undefined,
        };
      } else {
        populated[Number(index)] = step;
      }
    });

    return populated;
  }, [transformedConfigBase, videosMap, audiosMap]);

  // Calcola maxDuration dalla config trasformata
  const calculatedMaxDuration = useMemo(() => {
    let maxDuration = 0;

    Object.values(transformedConfig).forEach(step => {
      if ('video' in step && step.video?.duration) {
        maxDuration = Math.max(maxDuration, step.video.duration);
      }
      // Nota: Audio non ha duration nel tipo, ma se dovesse averla in futuro
      // if ('audio' in step && step.audio?.duration) {
      //   maxDuration = Math.max(maxDuration, step.audio.duration);
      // }
    });

    return maxDuration;
  }, [transformedConfig]);

  // Verifica se i media stanno ancora caricando
  const isMediaLoading = useMemo(() => {
    return (
      videoQueries.some(q => q.isLoading) || audioQueries.some(q => q.isLoading)
    );
  }, [videoQueries, audioQueries]);

  const session = useMemo<DeviceConfigurationApi | undefined>(() => {
    if (!sessionQuery.data) return undefined;

    const data = sessionQuery.data;
    const defaultVariation =
      data.config?.variation ||
      appConfigs[data.type as keyof typeof appConfigs]?.defaultVariation ||
      0;

    const mergedSession = {
      ...data,
      config: {
        ...data.config,
        variation: defaultVariation || undefined,
        maxDuration:
          calculatedMaxDuration ||
          data.config?.duration ||
          data.config?.maxDuration ||
          data.video?.duration ||
          0,
      },
      baseline: typeof data.baseline === 'number' ? data.baseline : 0,
      video: data.video,
    };
    return mergedSession;
  }, [sessionQuery.data, calculatedMaxDuration]);

  // Ref per tracciare le chiamate mutation e prevenire duplicati
  const mutationCallCounter = useRef(0);
  const pendingMutations = useRef<Set<string>>(new Set());

  // Reset counter e pending mutations quando cambia la sessione
  useEffect(() => {
    mutationCallCounter.current = 0;
    pendingMutations.current.clear();
  }, [id]);

  // Mutation for updating session
  const updateMutation = useMutation({
    mutationFn: async (updates: Partial<DeviceConfigurationApi>) => {
      if (!id) {
        throw new Error('Session ID is required');
      }

      const updateKey = JSON.stringify(updates);

      // Verifica se c'è già una chiamata identica in corso
      if (pendingMutations.current.has(updateKey)) {
        return;
      }

      pendingMutations.current.add(updateKey);

      try {
        await updateConfiguration(id, updates as UpdateConfigurationRequest);
      } finally {
        pendingMutations.current.delete(updateKey);
      }
    },
    onMutate: async updates => {
      // Cancella qualsiasi query in corso per evitare race conditions
      await queryClient.cancelQueries({ queryKey: sessionQueryKey(id) });

      // Snapshot del valore precedente per il rollback
      const previousSession = queryClient.getQueryData<DeviceConfigurationApi>(
        sessionQueryKey(id)
      );

      // Optimistic update: aggiorna la cache immediatamente
      if (previousSession) {
        queryClient.setQueryData<DeviceConfigurationApi>(sessionQueryKey(id), {
          ...previousSession,
          ...updates,
        });
      }

      return { previousSession };
    },
    onError: (_err, _updates, context) => {
      // In caso di errore, ripristina il valore precedente
      if (context?.previousSession) {
        queryClient.setQueryData(sessionQueryKey(id), context.previousSession);
      }
    },
    onSuccess: () => {
      // NON invalidare o refetchare - abbiamo già fatto l'optimistic update!
      // Invalidare causerebbe un loop infinito di richieste
    },
  });

  // Event manager - create dummy manager when session is not available
  const dummyEventManager = useMemo(
    () => ({
      init: () => {},
      start: () => {},
      stop: () => {},
      cancel: () => {},
      changeEvent: () => {},
    }),
    []
  );

  // Create event manager only when session is available
  const eventManager = useMemo(() => {
    if (!session) {
      return dummyEventManager;
    }
    return createSessionEventManager(
      session.type || 'default',
      id || '',
      session,
      sendMessage
    );
  }, [session, id, sendMessage, dummyEventManager]);

  // Baseline calculation hook
  const { startBaselineCalculation, baselineDefaultDuration } =
    useBaselineCalculation();
  const clearAllEvents = useHeartRateStore(s => s.clearAllEvents);

  // Subscribe to session events - crea subscription solo quando cambia id
  useEffect(() => {
    if (!id) {
      return;
    }

    // Set per notifiche ed eventi processati (uno per subscription)
    const shownNotifications = new Set<string>();
    const processedSessionEndedEvents = new Set<string>();

    const unsubscribe = subscribeToMessages(
      createSessionMessageFilter(
        id,
        session?.deviceCode,
        session?.type === 'exposure'
      ),
      message => {
        const messageName = extractMessageName(message);
        const payload = message.data as unknown;

        if (typeof payload !== 'object' || payload === null) {
          return;
        }

        // Usa i valori direttamente dalle closure (session, normalizedConfig, updateMutation, clearAllEvents)
        const currentSession = session;
        const currentNormalizedConfig =
          normalizedConfig.length > 0
            ? normalizedConfig
            : currentSession?.config
              ? normalizeInitSessionConfig(currentSession.config)
              : [];
        const currentUpdateMutation = updateMutation;
        const currentClearAllEvents = clearAllEvents;
        const exposureConfig =
          currentSession?.type === 'exposure' && currentSession
            ? getExposureConfigFromSession(currentSession)
            : undefined;

        if (!currentUpdateMutation) {
          return;
        }

        try {
          switch (messageName) {
            case 'sessionInfo':
              handleSessionInfo(payload as SessionInfoPayload, id, {
                session: currentSession,
                normalizedConfig: currentNormalizedConfig,
                updateMutation: currentUpdateMutation,
                shownNotifications,
              });
              break;
            case 'sessionEnded':
              handleSessionEnded(payload as SessionEndedPayload, id, {
                updateMutation: currentUpdateMutation,
                processedEvents: processedSessionEndedEvents,
                normalizedConfig: currentNormalizedConfig,
                session: currentSession,
              });
              break;
            case 'sessionStartedAt':
              handleSessionStartedAt(payload as SessionStartedAtPayload, id, {
                updateMutation: currentUpdateMutation,
              });
              if (currentSession?.type === 'exposure') {
                handleExposureEvent(
                  messageName,
                  payload as Record<string, unknown>,
                  id,
                  exposureConfig
                );
                const ts = (payload as Record<string, unknown>).timestamp;
                const toastKey = `sessionStartedAt-${id}-${ts}`;
                if (!shownExposureToastKeys.has(toastKey)) {
                  shownExposureToastKeys.add(toastKey);
                  notifications.show({
                    color: 'blue',
                    withBorder: true,
                    radius: 'lg',
                    title: 'Sessione avviata',
                    message: 'Il dispositivo ha avviato la sessione exposure.',
                    autoClose: 5000,
                  });
                }
              }
              break;
            case 'sessionEndedAt':
              if (currentSession?.type === 'exposure') {
                handleExposureEvent(
                  messageName,
                  payload as Record<string, unknown>,
                  id,
                  exposureConfig
                );
                const ts = (payload as Record<string, unknown>).timestamp;
                const toastKey = `sessionEndedAt-${id}-${ts}`;
                if (!shownExposureToastKeys.has(toastKey)) {
                  shownExposureToastKeys.add(toastKey);
                  notifications.show({
                    color: 'blue',
                    withBorder: true,
                    radius: 'lg',
                    title: 'Sessione terminata',
                    message:
                      'Il dispositivo ha terminato la sessione exposure.',
                    autoClose: 5000,
                  });
                }
              }
              break;
            case 'tutorialCompleted': {
              handleExposureEvent(
                messageName,
                payload as Record<string, unknown>,
                id,
                exposureConfig
              );
              const ts = (payload as Record<string, unknown>).timestamp;
              const toastKey = `tutorialCompleted-${id}-${ts}`;
              if (!shownExposureToastKeys.has(toastKey)) {
                shownExposureToastKeys.add(toastKey);
                notifications.show({
                  color: 'blue',
                  withBorder: true,
                  radius: 'lg',
                  title: 'Tutorial completato',
                  message: 'Il dispositivo ha completato il tutorial.',
                  autoClose: 5000,
                });
              }
              break;
            }
            case 'itemShown':
            case 'foodShown': {
              const pl = payload as Record<string, unknown>;
              handleExposureEvent('itemShown', pl, id, exposureConfig);
              const resolved = resolveExposureItemFromPayload(
                pl,
                exposureConfig
              );
              const isMultiItem = Boolean(exposureConfig?.isMultiItem);
              const ts = pl.timestamp;
              const toastKey = `itemShown-${id}-${ts}-${
                isMultiItem && resolved.portataNumber != null
                  ? `p${resolved.portataNumber}`
                  : resolved.itemIds
                      .slice()
                      .sort((a, b) => a - b)
                      .join('|')
              }`;
              if (!shownExposureToastKeys.has(toastKey)) {
                shownExposureToastKeys.add(toastKey);
                const { title, detail } = formatExposureItemShown({
                  itemIds: resolved.itemIds,
                  portataNumber: resolved.portataNumber,
                  isMultiItem,
                });
                notifications.show({
                  color: 'blue',
                  withBorder: true,
                  radius: 'lg',
                  title,
                  message: detail,
                  autoClose: 5000,
                });
              }
              break;
            }
            case 'itemFeedback':
            case 'foodFeedback': {
              const pl = payload as Record<string, unknown>;
              handleExposureEvent('itemFeedback', pl, id, exposureConfig);
              const resolved = resolveExposureItemFromPayload(
                pl,
                exposureConfig
              );
              const isMultiItem = Boolean(exposureConfig?.isMultiItem);
              const score =
                typeof pl.score === 'number' ? pl.score : Number(pl.score) || 0;
              const ts = pl.timestamp;
              const toastKey = `itemFeedback-${id}-${ts}-${
                isMultiItem && resolved.portataNumber != null
                  ? `p${resolved.portataNumber}`
                  : resolved.itemIds
                      .slice()
                      .sort((a, b) => a - b)
                      .join('|')
              }-${score}`;
              if (!shownExposureToastKeys.has(toastKey)) {
                shownExposureToastKeys.add(toastKey);
                const { title, detail } = formatExposureItemFeedback({
                  itemIds: resolved.itemIds,
                  portataNumber: resolved.portataNumber,
                  isMultiItem,
                  score,
                });
                notifications.show({
                  color: 'blue',
                  withBorder: true,
                  radius: 'lg',
                  title,
                  message: detail,
                  autoClose: 5000,
                });
              }
              break;
            }
            case 'mealEnded': {
              if (currentSession?.type === 'exposure') {
                handleExposureEvent(
                  messageName,
                  payload as Record<string, unknown>,
                  id,
                  exposureConfig
                );
                const ts = (payload as Record<string, unknown>).timestamp;
                const toastKey = `mealEnded-${id}-${ts}`;
                if (!shownExposureToastKeys.has(toastKey)) {
                  shownExposureToastKeys.add(toastKey);
                  notifications.show({
                    color: 'blue',
                    withBorder: true,
                    radius: 'lg',
                    title: 'Pasto terminato',
                    message: 'Il dispositivo ha segnalato la fine del pasto.',
                    autoClose: 5000,
                  });
                }
              }
              break;
            }
            case 'cancelSession':
              handleCancelSession(payload as CancelSessionReceivedPayload, id, {
                session: currentSession,
                updateMutation: currentUpdateMutation,
                clearAllEvents: currentClearAllEvents,
              });
              break;
          }
        } catch {
          // Errore gestito silenziosamente
        }
      }
    );

    return unsubscribe;
  }, [
    id,
    session,
    normalizedConfig,
    updateMutation,
    clearAllEvents,
    subscribeToMessages,
  ]);

  // Session actions
  const initSession = useCallback(() => {
    if (!session) return;

    updateMutation.mutate({ status: 'initializing' });
    eventManager.init();
  }, [session, updateMutation, eventManager]);

  const isStartingRef = useRef(false);
  const isTerminatingRef = useRef(false);

  const startExperience = useCallback(
    async (options?: StartExperienceOptions) => {
      if (!id || !session) {
        return;
      }

      if (isStartingRef.current) {
        return;
      }

      if (session?.status === 'in-progress') {
        return;
      }

      isStartingRef.current = true;

      const updates = {
        status: 'in-progress' as const,
        started_at: new Date().toISOString(),
      };

      const showStartedNotification = () => {
        notifications.show({
          id: 'start-session',
          color: 'teal',
          withBorder: true,
          radius: 'lg',
          title: 'Session started',
          autoClose: 2000,
          message: 'Configuration session started successfully',
        });
      };

      const showStartErrorNotification = (error: unknown) => {
        notifications.show({
          id: 'start-session-error',
          color: 'red',
          withBorder: true,
          radius: 'lg',
          title: 'An error occurred',
          autoClose: false,
          message:
            error instanceof Error ? error.message : 'Failed to start session',
        });
      };

      try {
        if (options?.persistInBackground) {
          void updateMutation
            .mutateAsync(updates)
            .then(() => {
              showStartedNotification();
            })
            .catch(error => {
              showStartErrorNotification(error);
            });

          if (!options.skipDeviceStart) {
            eventManager.start();
          }

          return;
        }

        await updateMutation.mutateAsync(updates);

        if (!options?.skipDeviceStart) {
          eventManager.start();
        }

        showStartedNotification();
      } catch (error) {
        showStartErrorNotification(error);
        throw error;
      } finally {
        isStartingRef.current = false;
      }
    },
    [id, session, updateMutation, eventManager]
  );

  const forceStartExperience = useCallback(() => {
    if (!session) {
      return;
    }
    eventManager.start();
    notifications.show({
      id: 'force-start-session',
      color: 'orange',
      withBorder: true,
      radius: 'lg',
      title: 'Avvio forzato',
      autoClose: 3000,
      message: 'Evento startSession reinviato al visore',
    });
  }, [session, eventManager]);

  const startBaseline = useCallback(() => {
    startBaselineCalculation(calculatedBaseline => {
      // Salva baseline e durata sulla sessione (campi dedicati, senza toccare config)
      updateMutation
        .mutateAsync({
          baseline: calculatedBaseline,
          baselineDuration: baselineDefaultDuration,
        } as Partial<DeviceConfigurationApi>)
        .then(() => startExperience())
        .catch(() => startExperience());
    });
  }, [
    startBaselineCalculation,
    startExperience,
    updateMutation,
    baselineDefaultDuration,
  ]);

  const skipBaseline = useCallback(() => {
    startExperience();
  }, [startExperience]);

  const terminateSession = useCallback(async () => {
    if (!id) return;

    if (isTerminatingRef.current) {
      return;
    }

    isTerminatingRef.current = true;

    try {
      eventManager.stop();

      const events = useHeartRateStore.getState().events;
      const phaseEvents = useHeartRateStore.getState().phaseEvents;
      const exposureEvents = useHeartRateStore.getState().exposureEvents;
      const baseline = useBaselineCalculationStore.getState().baseline;
      const baselineDuration =
        useBaselineCalculationStore.getState().baselineDefaultDuration;

      notifications.show({
        id: 'session-completed',
        color: 'green',
        withBorder: true,
        radius: 'lg',
        title: 'Session completed',
        autoClose: 2000,
        message: 'Session completed successfully',
      });

      const currentSession = queryClient.getQueryData<DeviceConfigurationApi>(
        sessionQueryKey(id)
      );
      const existingConfig = currentSession?.config ?? {};

      const savedData = {
        status: 'completed' as const,
        ended_at: new Date().toISOString(),
        hrData: normalizeEvents(events),
        results: phaseEvents,
        baseline,
        baselineDuration,
        exposureEvents: [...exposureEvents],
        config: buildCompletedSessionConfigJson(existingConfig, exposureEvents),
      };

      await updateMutation.mutateAsync(
        savedData as unknown as Partial<DeviceConfigurationApi>
      );
    } finally {
      isTerminatingRef.current = false;
    }
  }, [id, eventManager, updateMutation, queryClient]);

  const cancelSession = useCallback(async () => {
    if (!id) return;

    eventManager.cancel();

    notifications.show({
      id: 'session-aborted',
      color: 'red',
      withBorder: true,
      radius: 'lg',
      title: 'Session aborted',
      autoClose: 2000,
      message: 'Session aborted without saving data',
    });

    clearAllEvents();
    await updateMutation.mutateAsync({
      status: 'queued',
      started_at: null,
      ended_at: null,
      baseline: 0,
      hrData: [],
      results: [],
    });
  }, [id, eventManager, clearAllEvents, updateMutation]);

  // Mutation for deleting session
  const deleteMutation = useMutation({
    mutationFn: async (sessionId: number | string) => {
      await deleteConfiguration(sessionId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: sessionQueryKey(id),
        refetchType: 'active',
      });
      queryClient.invalidateQueries({
        queryKey: ['configurations'],
        refetchType: 'active',
      });
      notifications.show({
        color: 'green',
        withBorder: true,
        radius: 'lg',
        title: 'Sessione eliminata',
        message: 'La sessione è stata eliminata con successo',
        autoClose: 2000,
      });
      navigate('/sessions');
    },
    onError: (error: Error) => {
      notifications.show({
        color: 'red',
        withBorder: true,
        radius: 'lg',
        title: 'Errore',
        message:
          error.message || "Errore durante l'eliminazione della sessione",
        autoClose: false,
      });
    },
  });

  const deleteSession = useCallback(
    (sessionId: number | string) => {
      deleteMutation.mutate(sessionId);
    },
    [deleteMutation]
  );

  const setStatus = useCallback(
    (newStatus: SessionStatus) => {
      if (session?.status === newStatus) {
        return;
      }

      updateMutation.mutate({ status: newStatus });
    },
    [updateMutation, session?.status]
  );

  return {
    session,
    transformedConfig,
    isLoading: sessionQuery.isLoading || isMediaLoading,
    isFetching: sessionQuery.isFetching,
    isPending: updateMutation.isPending,
    error: sessionQuery.error,
    refetch: sessionQuery.refetch,
    // Actions
    initSession,
    startBaseline,
    startExperience,
    forceStartExperience,
    skipBaseline,
    terminateSession,
    cancelSession,
    deleteSession,
    setStatus,
    isDeleting: deleteMutation.isPending,
  };
};
